<template>
  <div class="base-tabs-wrapper" :style="{ marginBottom: bottomMargin }">
    <div class="base-tabs">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        class="tab-button"
        :class="{
          active: modelValue === tab.key,
          'tab-button--icon-only': tab.iconOnly,
          'tab-button--danger': tab.variant === 'danger',
          'tab-button--neutral': tab.variant === 'neutral',
        }"
        :aria-label="tab.ariaLabel || tab.label"
        :aria-current="tab.ariaCurrent"
        :title="tab.title || tab.label"
        @click="onTabClick(tab, $event)"
        @mousedown="onTabMouseDown(tab, $event)"
        @contextmenu.prevent="onTabContextMenu(tab, $event)"
      >
        <!-- Логотип вкладки -->
        <img
          v-if="tab.iconUrl"
          class="tab-image"
          :src="tab.iconUrl"
          alt=""
        />
        <!-- Иконка вкладки из набора AppIcon -->
        <AppIcon
          v-else-if="tab.iconName"
          class="tab-icon"
          :name="tab.iconName"
          :size="18"
        />
        <!-- Emoji-иконка вкладки -->
        <span v-else-if="tab.icon" class="tab-icon">{{ tab.icon }}</span>

        <span v-if="tab.label" class="tab-label">{{ tab.label }}</span>

        <span
          v-if="tab.badge !== undefined && tab.badge !== null"
          class="tab-badge"
        >
          {{ tab.badge }}
        </span>

        <span
          v-if="tab.suffixIcon"
          class="tab-suffix-icon"
          @click.stop="onSuffixClick(tab)"
          @mousedown.stop="onSuffixMouseDown(tab, $event)"
          v-html="tab.suffixIcon"
        ></span>
      </button>
    </div>

    <BaseContextMenu
      :visible="contextMenu.visible"
      :x="contextMenu.x"
      :y="contextMenu.y"
      :items="contextMenu.items"
      @close="closeContextMenu"
    />
  </div>
</template>

<script>
import AppIcon from "@/components/Icon/AppIcon.vue";
import {
  buildTabQuery,
  selectRouteTab,
} from "@/utils/tabRouteUtils";

export default {
  name: "BaseTabs",
  components: {
    AppIcon,
  },
  props: {
    tabs: {
      type: Array,
      required: true,
      // Структура таба:
      // {
      //   key: String,
      //   label: String,
      //   icon: String (эмодзи),
      //   iconUrl: String (URL логотипа),
      //   iconName: String (имя AppIcon),
      //   iconOnly: Boolean,
      //   badge: Number | null,
      //   variant: "danger" | "neutral" | undefined,
      //   suffixIcon: String|null (HTML строка SVG иконки),
      // }
    },
    modelValue: {
      type: String,
      required: true,
    },
    bottomMargin: {
      type: [String, Number],
      default: null,
    },
    // (tabKey) => [{ key, label, icon, action }]
    contextMenuItems: {
      type: Function,
      default: null,
    },
    // (tabKey) => Boolean - показывать ли дефолтный пункт "Открыть в новой вкладке"
    showDefaultContextItem: {
      type: Function,
      default: () => true,
    },
    // (tabKey) => Boolean — если true, родитель сам управляет activeTab для этого таба
    preventUpdate: {
      type: Function,
      default: null,
    },
    // Включается постепенно, пока не все страницы переведены на URL-вкладки.
    syncWithRoute: {
      type: Boolean,
      default: false,
    },
    // Позволяет нескольким наборам вкладок использовать разные query-параметры.
    routeQueryKey: {
      type: String,
      default: "tab",
    },
  },
  emits: [
    "update:modelValue",
    "middle-click",
    "tab-click",
    "suffix-click",
  ],
  data() {
    return {
      contextMenu: {
        visible: false,
        x: 0,
        y: 0,
        items: [],
      },
    };
  },
  mounted() {
    document.addEventListener("click", this.closeContextMenu);
    document.addEventListener("keydown", this.onKeyDown);
    this.syncTabWithRoute();
  },
  beforeUnmount() {
    document.removeEventListener("click", this.closeContextMenu);
    document.removeEventListener("keydown", this.onKeyDown);
  },
  methods: {
    onTabClick(tab, event) {
      if (event.ctrlKey || event.metaKey) {
        this.$emit("middle-click", {
          key: tab.key,
          fromSuffix: false,
        });
        return;
      }
      this.$emit("tab-click", { key: tab.key, fromSuffix: false });
      if (!this.preventUpdate || !this.preventUpdate(tab.key)) {
        this.$emit("update:modelValue", tab.key);
      }
    },

    onSuffixClick(tab) {
      this.$emit("suffix-click", tab.key);
      this.$emit("tab-click", { key: tab.key, fromSuffix: true });
    },

    onTabMouseDown(tab, event) {
      if (event.button === 1) {
        event.preventDefault();
        this.$emit("middle-click", {
          key: tab.key,
          fromSuffix: false,
        });
      }
    },

    onSuffixMouseDown(tab, event) {
      if (event.button === 1) {
        event.preventDefault();
        this.$emit("middle-click", {
          key: tab.key,
          fromSuffix: true,
        });
      }
    },

    // TODO: вынести общую механику context menu в helper/composable.
    onTabContextMenu(tab, event) {
      const showDefault = this.showDefaultContextItem(tab.key);

      const defaultItems = showDefault
        ? [
            {
              key: "open-new-tab",
              label: "Открыть в новой вкладке",
              icon: `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
            <polyline points="15 3 21 3 21 9"/>
            <line x1="10" y1="14" x2="21" y2="3"/>
          </svg>`,
              action: () =>
                this.$emit("middle-click", {
                  key: tab.key,
                  fromSuffix: false,
                }),
            },
          ]
        : [];

      const extraItems = this.contextMenuItems
        ? this.contextMenuItems(tab.key)
        : [];

      const allItems = [...defaultItems, ...extraItems];
      if (!allItems.length) return;

      const menuWidth = 220;
      const menuHeight = allItems.length * 44;
      let x = event.clientX;
      let y = event.clientY;

      if (x + menuWidth > window.innerWidth)
        x = window.innerWidth - menuWidth - 8;
      if (y + menuHeight > window.innerHeight)
        y = window.innerHeight - menuHeight - 8;

      this.contextMenu = {
        visible: true,
        x,
        y,
        items: allItems,
      };
    },

    closeContextMenu() {
      this.contextMenu.visible = false;
    },

    onKeyDown(event) {
      if (event.key === "Escape") {
        this.closeContextMenu();
      }
    },

    syncTabWithRoute() {
      if (!this.syncWithRoute) return;

      const availableTabs = this.tabs.map((tab) => tab.key);
      if (!availableTabs.length) return;

      const defaultTab = availableTabs.includes(this.modelValue)
        ? this.modelValue
        : availableTabs[0];
      const nextTab = selectRouteTab({
        requestedTab: this.$route.query[this.routeQueryKey],
        availableTabs,
        defaultTab,
      });

      if (nextTab !== this.modelValue) {
        this.$emit("update:modelValue", nextTab);
      }

      this.updateRouteTab(nextTab);
    },

    updateRouteTab(tab) {
      if (!this.syncWithRoute) return;
      if (this.$route.query[this.routeQueryKey] === tab) return;

      this.$router
        .replace({
          query: buildTabQuery({
            query: this.$route.query,
            queryKey: this.routeQueryKey,
            tab,
          }),
        })
        .catch(() => {});
    },
  },
  watch: {
    modelValue(tab) {
      this.updateRouteTab(tab);
    },

    "$route.query": {
      handler() {
        this.syncTabWithRoute();
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.base-tabs-wrapper {
  width: 100%;
  min-width: 0;
}

.base-tabs {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 6px;
  width: 100%;
}

.tab-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  flex: 0 1 auto;
  min-width: 132px;
  max-width: 100%;
  min-height: 36px;
  padding: 0 10px 0 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  color: #64748b;
  cursor: pointer;
  box-sizing: border-box;
  text-align: left;
  transition: background-color 0.1s ease, border-color 0.1s ease,
    box-shadow 0.1s ease;
}

.tab-button:hover:not(.active) {
  background: #e7f7f4;
  border-color: var(--color-green);
  color: var(--color-dark);
  box-shadow: 0 1px 3px rgba(0, 191, 165, 0.16);
}

.tab-button:focus-visible {
  outline: 2px solid var(--color-green);
  outline-offset: 2px;
}

.tab-button.active {
  background: var(--color-green);
  border-color: var(--color-green);
  color: #fff;
  box-shadow: 0 2px 5px rgba(0, 191, 165, 0.28);
}

.tab-button--icon-only {
  justify-content: center;
  min-width: 36px;
  padding: 0 8px;
}

.tab-button--icon-only .tab-icon,
.tab-button--icon-only .tab-image {
  margin-right: 0;
}

.tab-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 18px;
  margin-right: 6px;
  font-size: 16px;
  line-height: 1;
}

.tab-image {
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
  margin-right: 6px;
  object-fit: contain;
}

.tab-label {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  overflow-wrap: anywhere;
}

.tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  min-width: 18px;
  min-height: 18px;
  margin-left: 6px;
  padding: 0 6px;
  border-radius: 10px;
  background: #e7f7f4;
  color: var(--color-green-hover);
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  box-sizing: border-box;
}

.tab-button.active .tab-badge {
  background: #fff;
  color: var(--color-green-hover);
}

.tab-button--danger {
  background: #fff1f2;
  border-color: var(--color-danger);
  color: var(--color-danger-hover);
}

.tab-button--danger:hover:not(.active) {
  background: #ffe4e6;
  border-color: var(--color-danger-hover);
  color: var(--color-danger-hover);
  box-shadow: none;
}

.tab-button--danger:focus-visible {
  outline-color: var(--color-danger);
}

.tab-button--danger.active {
  background: var(--color-danger-hover);
  border-color: var(--color-danger-hover);
  color: #fff;
  box-shadow: none;
}

.tab-button--danger .tab-badge {
  background: var(--color-danger-hover);
  color: #fff;
}

.tab-button--danger.active .tab-badge {
  background: #fff;
  color: var(--color-danger-hover);
}

.tab-button--neutral {
  border-color: #9ca3af;
  color: #6b7280;
}

.tab-button--neutral:hover:not(.active),
.tab-button--neutral.active {
  background: #f3f4f6;
  border-color: #6b7280;
  color: #374151;
  box-shadow: none;
}

.tab-button--neutral:focus-visible {
  outline-color: #6b7280;
}

.tab-button--neutral .tab-badge {
  background: #e5e7eb;
  color: #4b5563;
}

.tab-suffix-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  margin-left: 6px;
  border-radius: 4px;
  color: inherit;
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.15s ease;
}

.tab-suffix-icon:hover {
  background: rgba(0, 191, 165, 0.16);
  transform: translateY(-1px);
}

.tab-button.active .tab-suffix-icon:hover {
  background: rgba(255, 255, 255, 0.24);
}

.tab-suffix-icon :deep(svg) {
  width: 14px;
  height: 14px;
}

.tab-button,
.tab-button:hover,
.tab-button.active,
.tab-button.active:hover {
  transition: background-color 0.1s ease, border-color 0.1s ease,
    box-shadow 0.1s ease !important;
}

.tab-label,
.tab-icon,
.tab-badge,
.tab-suffix-icon {
  transition: none !important;
}

@media (max-width: 767px) {
  .base-tabs {
    gap: 4px;
  }

  .tab-button {
    min-width: 0;
    flex: 1 1 calc(50% - 2px);
    min-height: 40px;
    padding: 7px 8px 7px 10px;
  }

  .tab-label {
    font-size: 13px;
  }

  .tab-icon {
    width: 16px;
    margin-right: 5px;
    font-size: 15px;
  }

  .tab-image {
    width: 16px;
    height: 16px;
    margin-right: 5px;
  }


  .tab-badge {
    margin-left: 5px;
  }

  .tab-suffix-icon {
    width: 20px;
    height: 20px;
    margin-left: 5px;
  }
}

@media (max-width: 420px) {
  .tab-button {
    flex-basis: 100%;
  }
}
</style>
